import React, { useState, useEffect, useMemo } from 'react';
import { Menu, Input, Popover, Tooltip, Typography } from 'antd';
import { ProfileOutlined, StarFilled } from '@ant-design/icons';
import Handlebars from 'handlebars';
import { htmlToText } from 'html-to-text';
import { StyledButton } from '../Button'
import { useCaseViewContext } from '../../context/CaseViewContext'

const MessageTemplateButton = ({ physicianDetails, setText, disabled }) => {
  const { caseDetails, apiClasses } = useCaseViewContext()
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);

  const templatesLookup = useMemo(() => templates.reduce((lookup, template) => {
    if (!(template._id in lookup)) {
      lookup[template._id] = template;
    }
    return lookup;
  }, {}), [templates])

  const sortFavorites = (templates) => {
    return [...templates].sort(a => a.favoritedBy ? -1 : 1);
  }

  const compileHandlebars = (message) => {
    const handlebarsTemplate = Handlebars.compile(message);

    return handlebarsTemplate({
      case: caseDetails,
      physician: physicianDetails,
      patient: caseDetails.patient
    })
  }

  const insertTemplate = (templateId) => {
    const template = templatesLookup[templateId];
    if (!template) return;
    setText(text => `${text}${compileHandlebars(htmlToText(template.message))}`);
  }

  const PREVIEW_MAX = 200;
  const previewMessage = (message) => {
    const text = htmlToText(message);
    return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX).trimEnd()}…` : text;
  }

  const handleSearchTemplates = (event) => {
    const search = event.target.value.toLowerCase();
    setFilteredTemplates(
      sortFavorites(templates.filter(template => {
        const inTitle = template.title.toLowerCase().includes(search);
        const inMessage = template.message.toLowerCase().includes(search);
        return inTitle || inMessage;
      }))
    );
  }

  useEffect(() => {
    setFilteredTemplates(sortFavorites(templates));
  }, [templates])

  useEffect(() => {
    // Try PhysicianAPI first, then fall back to TemplatesAPI for admin users
    const apiToUse = apiClasses?.PhysicianAPI || apiClasses?.TemplatesAPI
    if (apiToUse) {
      const getTemplatesMethod = apiToUse.getTemplates || apiToUse.getMessageTemplates
      if (getTemplatesMethod) {
        getTemplatesMethod.call(apiToUse, 'message')
          .then((res) => setTemplates(res.data?.templates || res.templates || []))
          .catch(() => setTemplates([]))
      } else {
        setTemplates([])
      }
    }
  }, [apiClasses])

  const PopOverMenu = () => (
    <Menu
      mode='inline'
      onClick={e => insertTemplate(e.key)}
      style={{
        ...filteredTemplates.length ? {} : { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        width: 400,
        height: 250,
        overflow: 'auto'
      }}>
      {
        !!filteredTemplates.length
          ? filteredTemplates.map(template => (
            <Menu.Item key={template._id}>
              <Tooltip placement='right' trigger='hover' title={previewMessage(template.message)}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {template.favoritedBy && <StarFilled style={{ color: '#7BAD7E' }} />}
                  <Typography.Text style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{template.title}</Typography.Text>
                </div>
              </Tooltip>
            </Menu.Item>
          ))
          : <h5 style={{ color: 'rgba(193, 188, 188, 0.05)', textAlign: 'center' }}>No Templates to Display</h5>
      }
    </Menu>
  )

  return (
    <Popover
      trigger="hover"
      placement='topLeft'
      content={
        <>
          <PopOverMenu />
          <Input id="message-template-search-input" placeholder='Find by title' onChange={handleSearchTemplates} />
        </>
      }
    >
      <StyledButton
        id="messaging-template-btn"
        size="medium"
        tooltip="Manage Templates"
        Icon={ProfileOutlined}
        disabled={disabled}
      />
    </Popover>
  )
}

export default MessageTemplateButton;
